import { type JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import useFetch, { FetchConfig, Status } from '../../../../hooks/fetch.hook';
import './style.scss';
import Spinner from '../../../Shared/Spinner';
import ValidationSummary from '../../../Shared/ValidationSummary';
import TextFieldFormGroup, { type ITextFieldFormGroupState } from '../../../Shared/TextFieldFormGroup';
import ValidationInfo from '../../../../validation/validation-info';
import type IValidatable from '../../../Shared/validatable.interface';
import { ValidatableComponent, ValidationHelper } from '../../../../helpers/validation.helper';
import { AnalyticsService } from '../../../../services/analytics-service';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import GeneralError from '../../../Shared/GeneralError';
import SchoolApiModel from '../../models/school.api-model';
import PageIntroduction from '../../Shared/PageIntroduction';
import InformationBox from '../../Shared/InformationBox';
import { InformationBoxType } from '../../Shared/InformationBox/information-box-type.enum';

function CreateSchoolNotAllowedErrorMessageBox(): JSX.Element {
  return (
    <InformationBox
      type={InformationBoxType.Error}
      headingElement={<span className="h3">Det gick inte att skapa skola</span>}
      textElement={
        <>
          <p className="mb-0">Ingen tävling pågår för tillfället.</p>
        </>
      }
      className="mb-3"
    />
  );
}

function EditSchool() {
  // === PARAMS ===
  const { schoolId } = useParams();
  const [searchParams] = useSearchParams();
  const municipalityGid = searchParams.get('gid');
  const navigate = useNavigate();

  // === STATE ===
  const [{ status: getSchoolStatus, data: getSchoolData, httpResponseStatusCode: schoolHttpResponseStatusCode }] = useFetch<SchoolApiModel>(
    `/api/paegnaben/municipality-administrator/schools/${schoolId}`,
    !schoolId,
    false,
    FetchConfig.default
  );
  const [getSchoolHasParticipations] = useFetch<boolean>(`/api/paegnaben/municipality-administrator/schools/${schoolId}/has-participations`, !schoolId);

  const [nameState, setNameState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [addressState, setAddressState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [postalCodeState, setPostalCodeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [cityState, setCityState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });

  const [saveStatus, setSaveStatus] = useState<Status>(Status.notperformed);
  const [saveErrorCode, setSaveErrorCode] = useState<number | undefined>(undefined);
  const [createdSchool, setCreatedSchool] = useState<SchoolApiModel | undefined>(undefined);
  const [createSchoolNotAllowedError, setCreateSchoolNotAllowedError] = useState(false);

  // === REFS ===
  const nameRef = useRef<IValidatable>(null!);
  const addressRef = useRef<IValidatable>(null!);
  const postalCodeRef = useRef<IValidatable>(null!);
  const municipalityRef = useRef<IValidatable>(null!);

  // === INTERFACE IMPLEMENTATION ===
  function getValidatableComponents(): {
    component: IValidatable;
    validationInfo: ValidationInfo;
    setState: React.Dispatch<React.SetStateAction<{ value: unknown; validationInfo: ValidationInfo }>>;
  }[] {
    const result: ValidatableComponent[] = [
      { component: nameRef.current, validationInfo: nameState.validationInfo, setState: setNameState },
      { component: addressRef.current, validationInfo: addressState.validationInfo, setState: setAddressState },
      { component: postalCodeRef.current, validationInfo: postalCodeState.validationInfo, setState: setPostalCodeState },
      { component: municipalityRef.current, validationInfo: cityState.validationInfo, setState: setCityState },
    ];

    return result;
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    if (!errors) {
      setCreateSchoolNotAllowedError(true);
      return;
    }
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, AnalyticsService.ErrorAction, e);
    });
  }

  // === EVENT HANDLERS ===
  async function handleSaveButtonClick(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();
    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined } | undefined;
    await flushSync(async () => {
      if (saveStatus === Status.loading) return;
      setSaveErrorCode(undefined);
      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, AnalyticsService.ErrorAction, e);
        });
        return;
      }
      setSaveStatus(Status.loading);
      const currentMuinicipalityGid = getSchoolData?.municipality ? getSchoolData.municipality.gid : municipalityGid;
      const schoolApiModel: SchoolApiModel = {
        id: getSchoolData ? Number(schoolId) : -1,
        name: nameState.value,
        streetAddress: addressState.value,
        postalCode: postalCodeState.value.replace(/\s/g, ''),
        city: cityState.value ?? '',
        municipalityGid: currentMuinicipalityGid ?? '',
      };
      const antiForgeryTokenElement = document.getElementById('antiForgeryToken') as HTMLInputElement;
      try {
        const response = await fetch(`/api/paegnaben/municipality-administrator/schools${schoolId ? `/${schoolId}` : ''}`, {
          method: schoolId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': antiForgeryTokenElement.value },
          body: JSON.stringify(schoolApiModel),
        });
        if (response.ok) {
          if (!schoolId) setCreatedSchool(await response.json());
          setSaveStatus(Status.success);
          setSaveErrorCode(undefined);
          submitResult = { status: Status.success, errorCode: undefined };
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, `Skola ${getSchoolData ? 'ändrad' : 'tillagd'}`, '');
        } else {
          setSaveStatus(Status.failed);
          setSaveErrorCode(response.status);
          submitResult = { status: Status.failed, errorCode: response.status };
          if (response.status === 400) {
            handleServerValidationErrors((await response.json()).errors);
          } else {
            AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, `Skola ${getSchoolData ? 'ändrad' : 'tillagd'} - misslyckat`, '');
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setSaveStatus(Status.failed);
        submitResult = { status: Status.failed, errorCode: error.status };
      }
    });
    if (!isFormValid || submitResult?.errorCode === 400) {
      document.getElementById('form')?.scrollIntoView();
      document.getElementById('form-validation-summary')?.focus();
    }
  }

  async function handleDeleteButtonClick(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();
    if (saveStatus === Status.loading) return;
    const antiForgeryTokenElement = document.getElementById('antiForgeryToken') as HTMLInputElement;
    try {
      const response = await fetch(`/api/paegnaben/municipality-administrator/schools/${schoolId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': antiForgeryTokenElement.value },
      });
      if (response.ok) {
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Skola borttagen', '');
        navigate(getSchoolData ? `/kommuner/${getSchoolData.municipality?.gid}?ds=${getSchoolData.id}&t=1` : '/', { state: getSchoolData });
      } else {
        setSaveStatus(Status.failed);
        setSaveErrorCode(response.status);
        if (response.status === 400) {
          handleServerValidationErrors((await response.json()).errors);
        }
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Skola borttagen - misslyckat', '');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setSaveStatus(Status.failed);
      setSaveErrorCode(error.status);
    }
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());
  useEffect(() => {
    if (getSchoolData) {
      const school = SchoolApiModel.mapToBusinessModel(getSchoolData);
      setNameState({ value: school.name, validationInfo: new ValidationInfo() });
      setAddressState({ value: school.streetAddress, validationInfo: new ValidationInfo() });
      setPostalCodeState({ value: school.postalCode, validationInfo: new ValidationInfo() });
      setCityState({ value: school.city, validationInfo: new ValidationInfo() });
    }
  }, [getSchoolData]);

  // === RENDER ===
  if (saveStatus === Status.success && !schoolId) {
    return <Navigate to={`/kommuner/${getSchoolData ? getSchoolData.municipality?.gid : municipalityGid}?t=1${createdSchool ? `&s=${createdSchool.id}` : ''}`} />;
  }
  if (saveStatus === Status.success && schoolId) {
    return <Navigate to={getSchoolData ? `/kommuner/${getSchoolData.municipality?.gid}?ss=${getSchoolData.id}&t=1` : '/'} state={getSchoolData} />;
  }

  if (saveStatus === Status.failed || getSchoolStatus === Status.failed) {
    if (saveErrorCode === 401 || schoolHttpResponseStatusCode === 401) {
      return <Navigate to="/session-utgangen" />;
    }
    if (saveErrorCode === 403 || schoolHttpResponseStatusCode === 403) {
      return <Navigate to="/atkomst-saknas" />;
    }
  }

  return (
    <div>
      {saveStatus === Status.notperformed && getSchoolStatus === Status.loading && <Spinner />}
      {getSchoolStatus === Status.failed && schoolId && <div>Det gick inte att hämta information om skolan. Försök igen senare.</div>}
      {(saveStatus !== Status.notperformed || getSchoolStatus === Status.success || !schoolId) && (
        <>
          <PageIntroduction heading={getSchoolData ? getSchoolData.name : 'Lägg till ny skola'} />
          <form id="form">
            <fieldset>
              <legend className="visually-hidden">Formulär för att {getSchoolData ? 'ändra uppgifter för' : 'lägga till'} skola</legend>
              <ValidationSummary id="form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
              {createSchoolNotAllowedError && <CreateSchoolNotAllowedErrorMessageBox />}
              <TextFieldFormGroup
                ref={nameRef}
                labelText="Skola"
                required={true}
                hideRequiredAsterisk={true}
                inputId="name-input"
                type="text"
                serverValidationName="name"
                state={[nameState, setNameState]}
                maxLength={35}
              />
              <TextFieldFormGroup
                ref={addressRef}
                labelText="Adress"
                required={true}
                hideRequiredAsterisk={true}
                inputId="address-input"
                type="text"
                serverValidationName="address"
                state={[addressState, setAddressState]}
                maxLength={35}
              />
              <div className="row">
                <TextFieldFormGroup
                  ref={postalCodeRef}
                  labelText="Postnummer"
                  className="col-md-6"
                  required={true}
                  hideRequiredAsterisk={true}
                  inputId="postal-code-input"
                  type="text"
                  serverValidationName="schoolPostalCode"
                  maxLength={6}
                  state={[postalCodeState, setPostalCodeState]}
                />
                <TextFieldFormGroup
                  ref={municipalityRef}
                  labelText="Ort"
                  className="col-md-6"
                  required={true}
                  hideRequiredAsterisk={true}
                  inputId="municipality-input"
                  type="text"
                  serverValidationName="municipality"
                  state={[cityState, setCityState]}
                  maxLength={27}
                />
              </div>
              <div className="d-grid d-md-flex gap-3">
                <Link to={`/kommuner/${getSchoolData ? getSchoolData.municipality?.gid : municipalityGid}?t=1`} className="btn btn-secondary flex-grow-1 flex-basis-0 order-2 order-md-1">
                  Avbryt
                </Link>
                <button type="button" className="btn btn-primary flex-grow-1 flex-basis-0 order-1 order-md-2" onClick={handleSaveButtonClick}>
                  {getSchoolData ? 'Spara ändringar' : 'Lägg till skola'}
                </button>
              </div>
              {getSchoolData && !getSchoolHasParticipations.data && (
                <button type="button" className="remove-school btn--as-link py-3" onClick={handleDeleteButtonClick}>
                  Ta bort skola
                </button>
              )}
              {saveStatus === Status.failed && saveErrorCode !== 400 && <GeneralError errorText="Kunde inte ändra skoluppgifter" className="mt-3" />}
            </fieldset>
          </form>
        </>
      )}
    </div>
  );
}

export default EditSchool;
